import {
  Layout,
  Sparkles,
  Boxes,
  Zap,
  Play,
  Bot,
  Image,
  FileCode,
  GitBranch,
  Split,
  Merge,
  Database,
  Globe,
  Clock,
  Webhook,
  AppWindow,
  Power,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Eye,
  Code,
  Mic,
  FileOutput,
  User,
  Send,
} from "lucide-react"

interface Feature {
  icon: React.ReactNode
  title: string
  description: string
}

interface FeatureShowcase {
  badge: string
  badgeIcon: React.ReactNode
  title: string
  description: string
  features: Feature[]
  mockup: React.ReactNode
  reversed?: boolean
}

/**
 * Visual Canvas mockup component.
 * Displays a node palette sidebar with canvas area.
 */
function CanvasMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-0 shadow-xl overflow-hidden">
      <div className="flex">
        {/* Node Palette Sidebar */}
        <div className="w-44 border-r border-neutral-200 bg-neutral-50 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-3">
            Node Types
          </div>

          {/* Triggers Category */}
          <div className="mb-3">
            <div className="text-xs text-neutral-400 mb-1.5">Triggers</div>
            <div className="space-y-1.5">
              {[
                { icon: <Clock className="h-3 w-3" />, label: "Schedule" },
                { icon: <Webhook className="h-3 w-3" />, label: "Webhook" },
              ].map((node, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded border border-neutral-200 bg-white px-2 py-1.5 cursor-grab hover:border-primary/30 transition-colors"
                >
                  <div className="rounded bg-yellow-100 p-1 text-yellow-600">
                    {node.icon}
                  </div>
                  <span className="text-xs text-neutral-700">{node.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AI Category */}
          <div className="mb-3">
            <div className="text-xs text-neutral-400 mb-1.5">AI Models</div>
            <div className="space-y-1.5">
              {[
                { icon: <Bot className="h-3 w-3" />, label: "Agent" },
                { icon: <Image className="h-3 w-3" />, label: "Image Gen" },
              ].map((node, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded border border-neutral-200 bg-white px-2 py-1.5 cursor-grab hover:border-primary/30 transition-colors"
                >
                  <div className="rounded bg-purple-100 p-1 text-purple-600">
                    {node.icon}
                  </div>
                  <span className="text-xs text-neutral-700">{node.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Control Flow */}
          <div>
            <div className="text-xs text-neutral-400 mb-1.5">Control</div>
            <div className="space-y-1.5">
              {[
                { icon: <GitBranch className="h-3 w-3" />, label: "Conditional" },
                { icon: <Split className="h-3 w-3" />, label: "Fork" },
              ].map((node, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded border border-neutral-200 bg-white px-2 py-1.5 cursor-grab hover:border-primary/30 transition-colors"
                >
                  <div className="rounded bg-blue-100 p-1 text-blue-600">
                    {node.icon}
                  </div>
                  <span className="text-xs text-neutral-700">{node.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 p-4 bg-neutral-100 min-h-[280px] relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000005_1px,transparent_1px),linear-gradient(to_bottom,#00000005_1px,transparent_1px)] bg-[size:16px_16px]" />

          {/* Sample connected nodes */}
          <div className="relative">
            <div className="absolute top-4 left-4 flex items-center gap-2 rounded-lg border border-yellow-200 bg-white px-2.5 py-1.5 shadow-sm">
              <div className="rounded bg-yellow-100 p-1">
                <Clock className="h-3 w-3 text-yellow-600" />
              </div>
              <span className="text-xs font-medium text-neutral-700">Schedule</span>
            </div>

            <svg className="absolute top-[38px] left-[120px] w-[60px] h-[50px]">
              <path d="M0,0 C30,0 30,50 60,50" fill="none" stroke="#D1D5DB" strokeWidth="2" />
            </svg>

            <div className="absolute top-[60px] left-[180px] flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-2.5 py-1.5 shadow-sm">
              <div className="rounded bg-purple-100 p-1">
                <Bot className="h-3 w-3 text-purple-600" />
              </div>
              <span className="text-xs font-medium text-neutral-700">AI Agent</span>
            </div>
          </div>

          {/* Drag indicator */}
          <div className="absolute bottom-4 right-4 text-xs text-neutral-400">
            Drag nodes to canvas
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * AI Generation mockup component.
 * Displays an AI chat interface for workflow generation.
 */
function AiGenerationMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden">
      {/* Chat Header */}
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-4 py-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium text-neutral-700">AI Assistant</span>
      </div>

      {/* Chat Messages */}
      <div className="p-4 space-y-4 min-h-[240px]">
        {/* User Message */}
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-lg bg-primary px-3 py-2">
            <p className="text-sm text-white">
              Create a workflow that monitors my inbox, classifies emails by urgency, and auto-replies to low priority ones.
            </p>
          </div>
        </div>

        {/* AI Response */}
        <div className="flex gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-3 w-3 text-primary" />
          </div>
          <div className="max-w-[80%]">
            <div className="rounded-lg bg-neutral-100 px-3 py-2">
              <p className="text-sm text-neutral-700 mb-2">
                I&apos;ll create that workflow for you. Here&apos;s what it will include:
              </p>
              <div className="space-y-1 text-xs text-neutral-600">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  <span>Gmail trigger for new emails</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  <span>AI classification node</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  <span>Conditional branching by urgency</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  <span>Auto-reply action for low priority</span>
                </div>
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              <div className="rounded border border-primary bg-primary/5 px-2 py-1 text-xs text-primary cursor-pointer hover:bg-primary/10">
                Generate Workflow
              </div>
              <div className="rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-600 cursor-pointer hover:bg-neutral-50">
                Modify
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t border-neutral-200 p-3">
        <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
          <input
            type="text"
            placeholder="Describe your workflow..."
            className="flex-1 bg-transparent text-sm text-neutral-700 placeholder:text-neutral-400 outline-none"
            disabled
          />
          <Send className="h-4 w-4 text-neutral-400" />
        </div>
      </div>
    </div>
  )
}

/**
 * Node Types mockup component.
 * Displays a grid of available node types.
 */
function NodeTypesMockup() {
  const nodeCategories = [
    {
      title: "AI Models",
      nodes: [
        { icon: <Bot className="h-4 w-4" />, label: "Agent", color: "bg-purple-100 text-purple-600" },
        { icon: <Image className="h-4 w-4" />, label: "Image Gen", color: "bg-pink-100 text-pink-600" },
        { icon: <Database className="h-4 w-4" />, label: "Embedding", color: "bg-indigo-100 text-indigo-600" },
        { icon: <Mic className="h-4 w-4" />, label: "TTS", color: "bg-violet-100 text-violet-600" },
      ],
    },
    {
      title: "Code",
      nodes: [
        { icon: <FileCode className="h-4 w-4" />, label: "JavaScript", color: "bg-yellow-100 text-yellow-600" },
        { icon: <Code className="h-4 w-4" />, label: "Python", color: "bg-blue-100 text-blue-600" },
      ],
    },
    {
      title: "Control Flow",
      nodes: [
        { icon: <GitBranch className="h-4 w-4" />, label: "Conditional", color: "bg-cyan-100 text-cyan-600" },
        { icon: <Split className="h-4 w-4" />, label: "Fork", color: "bg-teal-100 text-teal-600" },
        { icon: <Merge className="h-4 w-4" />, label: "Join", color: "bg-emerald-100 text-emerald-600" },
      ],
    },
    {
      title: "Data",
      nodes: [
        { icon: <FileOutput className="h-4 w-4" />, label: "Structured", color: "bg-orange-100 text-orange-600" },
        { icon: <Globe className="h-4 w-4" />, label: "Web Scrape", color: "bg-red-100 text-red-600" },
        { icon: <Database className="h-4 w-4" />, label: "Retrieval", color: "bg-amber-100 text-amber-600" },
      ],
    },
  ]

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      <div className="space-y-5">
        {nodeCategories.map((category, i) => (
          <div key={i}>
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-2">
              {category.title}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {category.nodes.map((node, j) => (
                <div
                  key={j}
                  className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 hover:border-primary/30 transition-colors"
                >
                  <div className={`rounded p-1.5 ${node.color}`}>
                    {node.icon}
                  </div>
                  <span className="text-sm font-medium text-neutral-700">{node.label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Triggers mockup component.
 * Displays trigger configuration options.
 */
function TriggersMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Trigger Types */}
      <div className="space-y-4">
        {/* Schedule Trigger */}
        <div className="rounded-lg border-2 border-primary bg-primary/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="rounded bg-primary/10 p-1.5">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <span className="font-medium text-neutral-900">Schedule Trigger</span>
            </div>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Active</span>
          </div>
          <div className="rounded border border-neutral-200 bg-white px-3 py-2 font-mono text-sm text-neutral-600">
            0 9 * * 1-5
          </div>
          <div className="mt-2 text-xs text-neutral-500">
            Runs every weekday at 9:00 AM
          </div>
        </div>

        {/* Webhook Trigger */}
        <div className="rounded-lg border border-neutral-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="rounded bg-blue-100 p-1.5">
                <Webhook className="h-4 w-4 text-blue-600" />
              </div>
              <span className="font-medium text-neutral-900">Webhook Trigger</span>
            </div>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-500">Inactive</span>
          </div>
          <div className="rounded border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 truncate">
            https://api.taqat.ai/webhook/abc123
          </div>
        </div>

        {/* App Trigger */}
        <div className="rounded-lg border border-neutral-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="rounded bg-orange-100 p-1.5">
                <AppWindow className="h-4 w-4 text-orange-600" />
              </div>
              <span className="font-medium text-neutral-900">App Trigger</span>
            </div>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Active</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded bg-[#4A154B] flex items-center justify-center">
              <span className="text-xs font-bold text-white">#</span>
            </div>
            <span className="text-sm text-neutral-700">On new Slack message</span>
          </div>
        </div>
      </div>

      {/* Activation Toggle */}
      <div className="mt-4 flex items-center justify-between rounded-lg bg-neutral-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <Power className="h-4 w-4 text-neutral-500" />
          <span className="text-sm text-neutral-700">Workflow Status</span>
        </div>
        <div className="h-5 w-9 rounded-full bg-primary">
          <div className="h-4 w-4 translate-x-4 translate-y-0.5 rounded-full bg-white shadow" />
        </div>
      </div>
    </div>
  )
}

/**
 * Execution mockup component.
 * Displays real-time execution status.
 */
function ExecutionMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <Play className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-neutral-700">Execution Log</span>
        </div>
        <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          Running...
        </span>
      </div>

      {/* Execution Steps */}
      <div className="p-4 space-y-3">
        {/* Completed Node */}
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-900">Webhook Trigger</span>
              <span className="text-xs text-green-600">Completed</span>
            </div>
            <div className="text-xs text-neutral-500 truncate">Received request from api.example.com</div>
          </div>
          <Eye className="h-4 w-4 text-neutral-400 cursor-pointer hover:text-neutral-600" />
        </div>

        {/* Completed Node */}
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-900">Classify Intent</span>
              <span className="text-xs text-green-600">Completed</span>
            </div>
            <div className="text-xs text-neutral-500 truncate">Intent: support_request (0.95)</div>
          </div>
          <Eye className="h-4 w-4 text-neutral-400 cursor-pointer hover:text-neutral-600" />
        </div>

        {/* Running Node */}
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-900">Generate Response</span>
              <span className="text-xs text-blue-600">Running</span>
            </div>
            <div className="text-xs text-neutral-500 truncate">Processing with GPT-4o...</div>
          </div>
          <Eye className="h-4 w-4 text-neutral-400 cursor-pointer hover:text-neutral-600" />
        </div>

        {/* Pending Node */}
        <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 opacity-60">
          <div className="h-4 w-4 rounded-full border-2 border-neutral-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-700">Send Email</span>
              <span className="text-xs text-neutral-500">Pending</span>
            </div>
            <div className="text-xs text-neutral-400">Waiting for previous node</div>
          </div>
        </div>
      </div>

      {/* Output Preview */}
      <div className="border-t border-neutral-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <User className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-xs font-medium text-neutral-700">Output Preview</span>
        </div>
        <div className="rounded bg-neutral-900 p-3 font-mono text-xs text-neutral-300">
          <span className="text-purple-400">{"{"}</span>
          <br />
          <span className="text-neutral-500 ml-2">&quot;intent&quot;:</span> <span className="text-green-400">&quot;support_request&quot;</span>
          <span className="text-neutral-500">,</span>
          <br />
          <span className="text-neutral-500 ml-2">&quot;confidence&quot;:</span> <span className="text-amber-400">0.95</span>
          <br />
          <span className="text-purple-400">{"}"}</span>
        </div>
      </div>
    </div>
  )
}

const showcases: FeatureShowcase[] = [
  {
    badge: "Visual Canvas",
    badgeIcon: <Layout className="h-3.5 w-3.5" />,
    title: "Drag, Drop, Connect",
    description:
      "Build workflows visually with our intuitive canvas. Simply drag nodes from the palette, drop them on the canvas, and connect them to create powerful automation flows.",
    features: [
      {
        icon: <Layout className="h-5 w-5" />,
        title: "Intuitive Canvas",
        description: "Drag-and-drop interface anyone can use",
      },
      {
        icon: <Boxes className="h-5 w-5" />,
        title: "20+ Node Types",
        description: "Organized by category for easy discovery",
      },
      {
        icon: <GitBranch className="h-5 w-5" />,
        title: "Visual Connections",
        description: "Draw edges between nodes to define flow",
      },
      {
        icon: <Zap className="h-5 w-5" />,
        title: "Real-time Feedback",
        description: "Instant validation and error highlighting",
      },
    ],
    mockup: <CanvasMockup />,
  },
  {
    badge: "AI Generation",
    badgeIcon: <Sparkles className="h-3.5 w-3.5" />,
    title: "Describe It, We Build It",
    description:
      "Let AI generate your workflow from natural language descriptions. Simply tell our AI assistant what you want to automate, and watch as it creates the complete workflow for you.",
    features: [
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: "Natural Language",
        description: "Describe workflows in plain English",
      },
      {
        icon: <Bot className="h-5 w-5" />,
        title: "AI Generation",
        description: "Complete workflows generated automatically",
      },
      {
        icon: <User className="h-5 w-5" />,
        title: "Chat Interface",
        description: "Conversational workflow building",
      },
      {
        icon: <GitBranch className="h-5 w-5" />,
        title: "Iterative Refinement",
        description: "Modify and improve through conversation",
      },
    ],
    mockup: <AiGenerationMockup />,
    reversed: true,
  },
  {
    badge: "Node Types",
    badgeIcon: <Boxes className="h-3.5 w-3.5" />,
    title: "Every Building Block You Need",
    description:
      "From AI models to code execution, from data processing to control flow - our comprehensive node library has everything you need to build any automation.",
    features: [
      {
        icon: <Bot className="h-5 w-5" />,
        title: "AI Models",
        description: "Agent, Image Gen, Embedding, TTS",
      },
      {
        icon: <Code className="h-5 w-5" />,
        title: "Code Execution",
        description: "Run JavaScript & Python securely",
      },
      {
        icon: <GitBranch className="h-5 w-5" />,
        title: "Control Flow",
        description: "Conditional, Fork, Join, Loop",
      },
      {
        icon: <Database className="h-5 w-5" />,
        title: "Data Processing",
        description: "Structured Output, Web Scrape, Retrieval",
      },
    ],
    mockup: <NodeTypesMockup />,
  },
  {
    badge: "Triggers",
    badgeIcon: <Zap className="h-3.5 w-3.5" />,
    title: "Run On Your Schedule",
    description:
      "Automate when and how your workflows run. Set up scheduled triggers, expose webhooks for external systems, or connect to app events - all with simple configuration.",
    features: [
      {
        icon: <Clock className="h-5 w-5" />,
        title: "Schedule Triggers",
        description: "Cron expressions for precise timing",
      },
      {
        icon: <Webhook className="h-5 w-5" />,
        title: "Webhook Triggers",
        description: "HTTP endpoints for external systems",
      },
      {
        icon: <AppWindow className="h-5 w-5" />,
        title: "App Triggers",
        description: "React to events from connected apps",
      },
      {
        icon: <Power className="h-5 w-5" />,
        title: "Instant Control",
        description: "Activate or deactivate anytime",
      },
    ],
    mockup: <TriggersMockup />,
    reversed: true,
  },
  {
    badge: "Execution",
    badgeIcon: <Play className="h-3.5 w-3.5" />,
    title: "Watch Your Workflow Run",
    description:
      "Get real-time visibility into your workflow execution. See the status of each node, inspect outputs, and debug issues with our comprehensive execution monitoring.",
    features: [
      {
        icon: <Play className="h-5 w-5" />,
        title: "Streaming Results",
        description: "Watch execution in real-time",
      },
      {
        icon: <CheckCircle2 className="h-5 w-5" />,
        title: "Visual Status",
        description: "Color-coded node execution states",
      },
      {
        icon: <Eye className="h-5 w-5" />,
        title: "Output Inspection",
        description: "View input/output at each step",
      },
      {
        icon: <AlertCircle className="h-5 w-5" />,
        title: "Error Handling",
        description: "Clear error messages and debugging",
      },
    ],
    mockup: <ExecutionMockup />,
  },
]

/**
 * Features section displaying the five main workflow builder capabilities.
 * Uses alternating left/right layout with mockups and feature lists.
 */
export function FeaturesSection() {
  return (
    <section className="bg-neutral-50 py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            Everything You Need to Automate Anything
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            Our visual workflow builder gives you complete control over your
            automations, from simple tasks to complex multi-step processes.
          </p>
        </div>

        {/* Feature Showcases */}
        <div className="space-y-24">
          {showcases.map((showcase, index) => (
            <div
              key={index}
              className={`grid gap-12 lg:grid-cols-2 lg:gap-16 ${
                showcase.reversed ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Text Content */}
              <div
                className={`flex flex-col justify-center ${
                  showcase.reversed ? "lg:order-2" : ""
                }`}
              >
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
                  {showcase.badgeIcon}
                  {showcase.badge}
                </div>
                <h3 className="mb-4 text-2xl font-bold text-neutral-900 lg:text-3xl">
                  {showcase.title}
                </h3>
                <p className="mb-8 text-neutral-600">{showcase.description}</p>

                {/* Feature List */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {showcase.features.map((feature, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        {feature.icon}
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">
                          {feature.title}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mockup */}
              <div className={showcase.reversed ? "lg:order-1" : ""}>
                {showcase.mockup}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
